import { ArrowRight, FileSearch, Scale, ShieldCheck } from "lucide-react";

const servicePillars = [
  {
    icon: FileSearch,
    title: "Legal Due Diligence",
    description: "Independent verification of property titles, ownership history, and land registry records before any purchase is completed."
  },
  {
    icon: Scale,
    title: "Transaction Structuring",
    description: "Legal guidance in negotiating property acquisitions, drafting contracts, and ensuring enforceable agreements."
  },
  {
    icon: ShieldCheck,
    title: "Secure Investment Advisory",
    description: "Strategic advisory for local and diaspora investors seeking safe and structured real estate investments in Nigeria."
  }
];

export const TrustedCounselSection = () => {
  return (
    <section 
      className="py-16 md:py-24 lg:py-32 bg-[#0A0A0B] relative overflow-hidden"
      data-testid="trusted-counsel-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      {/* Decorative corner accents */}
      <div className="absolute top-12 left-12 w-20 h-20 border-l border-t border-[#D4AF37]/15 hidden lg:block" />
      <div className="absolute bottom-12 right-12 w-20 h-20 border-r border-b border-[#D4AF37]/15 hidden lg:block" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            Strategic Legal Advisory
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white mt-4 md:mt-6 leading-tight">
            Trusted Legal Counsel
            <span className="block text-[#D4AF37] italic mt-2">for Property Investors</span>
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
        </div>

        {/* Introduction Text */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-[1.9] md:leading-[2] mb-5">
            Property investment in Nigeria requires careful legal oversight to avoid defective titles, 
            ownership disputes, and fraudulent transactions.
          </p>
          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-[1.9] md:leading-[2]">
            4 ACES Attorneys provides strategic legal advisory for investors seeking secure property 
            acquisitions and structured real estate transactions.
          </p>
        </div>

        {/* Service Pillars */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {servicePillars.map((pillar, index) => (
            <div 
              key={index}
              className="group text-center p-6 md:p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 bg-[#0A0A0B]/50 transition-all duration-300"
              data-testid={`service-pillar-${index}`}
            >
              <div className="w-14 h-14 md:w-16 md:h-16 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5 md:mb-6 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all duration-300">
                <pillar.icon className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-white mb-3 md:mb-4">
                {pillar.title}
              </h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Credibility Statement */}
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
          <p className="text-white/50 text-sm md:text-base italic leading-relaxed">
            "Our firm regularly advises private investors, property developers, and diaspora clients 
            seeking legally secure property transactions."
          </p>
        </div>

        {/* Trust Emphasis Statement - Premium Highlighted Note */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <div className="inline-block px-8 md:px-10 py-5 md:py-6 border border-[#D4AF37]/40 bg-[#111111] shadow-lg">
            <p className="text-[#D4AF37] text-sm md:text-base font-medium italic tracking-wide leading-relaxed">
              Every property transaction handled by our firm undergoes independent legal verification before completion.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a
            href="#booking"
            className="inline-flex items-center gap-2 md:gap-3 bg-[#D4AF37] text-[#0A0A0B] px-8 md:px-10 py-4 md:py-5 font-semibold text-sm tracking-wide hover:bg-[#C5A028] transition-colors"
            data-testid="trusted-counsel-cta"
          >
            Book a Private Consultation
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};
